import * as Sentry from '@sentry/react'
import {
	QueryClient,
	useMutation,
	type DefaultError,
	type UseMutationOptions,
	type UseMutationResult,
} from '@tanstack/react-query'
import { createTRPCClient, createWSClient, loggerLink, wsLink } from '@trpc/client'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { useMemo } from 'react'
import type { AppRouter } from '../../../companion/lib/UI/TRPC.js' // Type only import the router

import { setupConnectionLifecycle } from './ConnectionLifecycle.js'
import { makeAbsolutePath } from './util.js'

export type { AppRouter, RouterInput, RouterOutput } from '../../../companion/lib/UI/TRPC.js' // Type only import the router

export const queryClient = new QueryClient()

// Build a full url. This is needed to support older chromium, modern browsers support simply /trpc
let trpcUrl = window.location.origin + makeAbsolutePath(`/trpc`)
if (trpcUrl.startsWith('http')) trpcUrl = 'ws' + trpcUrl.slice(4)

const WS_CONNECT_TIMEOUT_MS = 10_000

/**
 * WebSocket subclass that self-aborts if the connection handshake does not
 * complete within a timeout. Works around Safari/iOS leaving sockets in
 * CONNECTING forever (no open/error event), which permanently wedges the
 * trpc ws client's reconnect loop (it has no connect timeout of its own).
 * Verified against @trpc/client 11.17.0.
 */
class WebSocketWithConnectTimeout extends WebSocket {
	constructor(url: string | URL, protocols?: string | string[]) {
		super(url, protocols)

		const timeout = setTimeout(() => {
			if (this.readyState === WebSocket.CONNECTING) {
				console.warn('WebSocket connect timed out, aborting')
				try {
					this.close()
				} catch (_e) {
					// ignore
				}
			}
		}, WS_CONNECT_TIMEOUT_MS)

		const clear = () => clearTimeout(timeout)
		this.addEventListener('open', clear)
		this.addEventListener('close', clear)
		this.addEventListener('error', clear)
	}
}

export const trpcWsClient = createWSClient({
	url: trpcUrl,
	WebSocket: WebSocketWithConnectTimeout,
	keepAlive: {
		enabled: true,
	},
	onError: (error) => {
		// This is probably pretty noisy, but there are some issues around here that need debugging
		Sentry.captureException(error, { tags: { area: 'trpc-ws-client' } })
	},
})

// Close the WebSocket connection on real unloads, and recover it after bfcache restores
setupConnectionLifecycle(trpcWsClient)

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		...(import.meta.env.DEV ? [loggerLink()] : []), // Enable logger in debug
		wsLink({
			client: trpcWsClient,
		}),
		// httpBatchLink({
		// 	url: '/trpc',
		// 	// You can pass any HTTP headers you wish here
		// 	// async headers() {
		// 	// 	return {
		// 	// 		authorization: getAuthCookie(),
		// 	// 	}
		// 	// },
		// }),
	],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
})

/**
 * A wrapper around `useMutation` that memoizes the returned values.
 * Without this it is very hard to use this in a useMemo or similar hook, due to the `useMutation` returning a new object on every render,
 * and eslint complaining about the dependency unless the mutation object is not destructured.
 * Note: this is intentionally minimal values so that it doesnt invalidate when the mutation is used
 */
export function useMutationExt<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
	options: UseMutationOptions<TData, TError, TVariables, TContext>,
	queryClient?: QueryClient
): Pick<UseMutationResult<TData, TError, TVariables, TContext>, 'mutateAsync' | 'mutate'> {
	const rawUseMutation = useMutation(options, queryClient)

	return useMemo(
		() => ({
			mutateAsync: rawUseMutation.mutateAsync,
			mutate: rawUseMutation.mutate,
		}),
		[rawUseMutation.mutateAsync, rawUseMutation.mutate]
	)
}
